import React, { useMemo } from 'react';
import { MdChevronLeft, MdBarChart } from 'react-icons/md';
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import { useProfile } from '../contexts/ProfileContext';
import { computeBudgetRule } from '../utils/computeBudgetRule';

// BeHuman accent palette — see styles/DESIGN.md.
// Hex literals because Recharts passes these to SVG `fill` attrs which
// don't resolve CSS vars. Keep these in sync with theme.css.
// Needs = teal (essentials/grounded), Savings = green (growth/positive),
// Debt & Giving = brown (borrowing/earthy), Discretionary = gold (luxury).
const TREND_COLORS = {
  'Needs':         '#448889', // td-teal
  'Savings':       '#2FBF00', // td-green
  'Debt & Giving': '#7C5636', // td-brown
  'Discretionary': '#CFBD91', // td-gold
};

const BudgetRuleScreen = ({ onNavigate }) => {
  const { loadedProfileData } = useProfile();
  const budgetData = useMemo(() => {
    if (loadedProfileData?.profile) {
      return computeBudgetRule(loadedProfileData.profile);
    }
    return null;
  }, [loadedProfileData]);

  const fmt = v => '$' + Math.round(v).toLocaleString();
  const tipStyle = { background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: 6, fontSize: 11, color: 'var(--text-dark)' };

  if (!budgetData) {
    return (
      <div className="screen" style={{ background: 'var(--bg-screen)' }}>
        <div className="simple-header">
          <MdChevronLeft className="back-icon" onClick={() => onNavigate('home')} />
          <h2 className="header-title">70/20/10 Rule</h2>
        </div>
        <div className="scroll-content gray-bg" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 60, textAlign: 'center', gap: 12 }}>
          <MdBarChart size={48} style={{ color: 'var(--text-medium)' }} />
          <h3 style={{ color: 'var(--text-dark)', margin: 0 }}>No Profile Loaded</h3>
          <p style={{ color: 'var(--text-medium)', fontSize: 13, maxWidth: 240, lineHeight: 1.5 }}>
            Load a customer profile in the Chat screen to see their 70/20/10 budget breakdown.
          </p>
        </div>
      </div>
    );
  }

  const {
    avgIncome, avgNeeds, avgSavings, avgDebt, avgDisc,
    pctNeeds, pctSavings, pctDebt, pctDisc,
    targetNeeds, targetSavings, targetDebt,
    monthlyData, insights,
  } = budgetData;

  const donutData = [
    { name: 'Needs',         value: avgNeeds,    color: TREND_COLORS['Needs'] },
    { name: 'Savings',       value: avgSavings,  color: TREND_COLORS['Savings'] },
    { name: 'Debt & Giving', value: avgDebt,     color: TREND_COLORS['Debt & Giving'] },
    { name: 'Discretionary', value: avgDisc,     color: TREND_COLORS['Discretionary'] },
  ].filter(d => d.value > 0);

  const RULE_CARDS = [
    {
      label: 'Needs',
      desc: 'Housing, utilities, groceries, transport, insurance, childcare',
      actual: avgNeeds, pct: pctNeeds, target: 70, targetAmt: targetNeeds,
      color: TREND_COLORS['Needs'], overIsBad: true,
    },
    {
      label: 'Savings',
      desc: 'RRSP, TFSA, savings transfers, investments',
      actual: avgSavings, pct: pctSavings, target: 20, targetAmt: targetSavings,
      color: TREND_COLORS['Savings'], overIsBad: false,
    },
    {
      label: 'Debt & Giving',
      desc: 'Loan payments, interest charges, charitable giving',
      actual: avgDebt, pct: pctDebt, target: 10, targetAmt: targetDebt,
      color: TREND_COLORS['Debt & Giving'], overIsBad: true,
    },
  ];

  return (
    <div className="screen" style={{ background: 'var(--bg-screen)' }}>
      <div className="simple-header">
        <MdChevronLeft className="back-icon" onClick={() => onNavigate('home')} />
        <h2 className="header-title">70/20/10 Rule</h2>
      </div>

      <div className="scroll-content gray-bg">

        <div className="budget-banner">
          <div>
            <div className="budget-banner-name">{loadedProfileData?.name || 'Customer'}</div>
            <div className="budget-banner-sub">Avg monthly after-tax income</div>
          </div>
          <div className="budget-banner-income">{fmt(avgIncome)}<span className="budget-banner-mo">/mo</span></div>
        </div>

        <div className="viz-chart-card" style={{ marginTop: 0, borderRadius: '0 0 10px 10px' }}>
          <div className="viz-chart-title">Actual Allocation</div>
          <div className="viz-chart-sub">Monthly average across all accounts</div>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={donutData} dataKey="value" nameKey="name"
                   cx="50%" cy="45%"
                   innerRadius={70} outerRadius={88}
                   stroke="#000000" strokeWidth={1}>
                {donutData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
              </Pie>
              <Tooltip formatter={(v, n) => [fmt(v) + '/mo', n]} contentStyle={tipStyle} />
              <Legend wrapperStyle={{ fontSize: 11 }} iconSize={9} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div style={{ padding: '12px 12px 0' }}>
          <div className="budget-section-label">Target vs Actual</div>
        </div>

        {RULE_CARDS.map(({ label, desc, actual, pct, target, targetAmt, color, overIsBad }) => {
          const isOnTarget = overIsBad ? pct <= target : pct >= target;
          const isClose    = overIsBad ? pct <= target + 5 : pct >= target * 0.85;
          // Semantic hex (kept as literals for the `+ '28'` alpha trick used below).
          // Maps: on-target → TD green, close → amber, over → red.
          const statusColor = isOnTarget ? '#2FBF00' : isClose ? '#F59E0B' : '#D32F2F';
          const statusText  = isOnTarget
            ? 'On Track'
            : isClose
            ? (overIsBad ? 'Slightly Over' : 'Nearly There')
            : (overIsBad ? 'Over Target' : 'Under Target');

          const diff = Math.abs(actual - targetAmt);
          const diffLabel = actual > targetAmt
            ? `$${diff.toLocaleString()}/mo over target`
            : actual < targetAmt
            ? `$${diff.toLocaleString()}/mo below target`
            : 'Exactly on target';

          const scale     = target * 1.4;
          const fillPct   = Math.min((pct / scale) * 100, 100);
          const markerPct = (target / scale) * 100;

          return (
            <div key={label} className="budget-rule-card" style={{ borderLeftColor: color }}>
              <div className="budget-rule-header">
                <div>
                  <div className="budget-rule-label" style={{ color }}>{label} <span className="budget-rule-target-pct">(target {target}%)</span></div>
                  <div className="budget-rule-desc">{desc}</div>
                </div>
                <div className="budget-rule-status" style={{ background: statusColor + '28', color: statusColor }}>{statusText}</div>
              </div>

              <div className="budget-bar-track">
                <div className="budget-bar-fill" style={{ width: `${fillPct}%`, background: isOnTarget ? color : statusColor }} />
                <div className="budget-bar-marker" style={{ left: `${markerPct}%` }} />
              </div>

              <div className="budget-bar-row">
                <span style={{ color: 'var(--text-dark)', fontWeight: 600, fontSize: 13 }}>
                  {pct}% <span style={{ fontWeight: 400, color: 'var(--text-medium)', fontSize: 12 }}>({fmt(actual)}/mo)</span>
                </span>
                <span style={{ color: 'var(--text-medium)', fontSize: 11 }}>Target: {fmt(targetAmt)}</span>
              </div>
              <div style={{ fontSize: 11, fontWeight: 600, marginTop: 3, color: isOnTarget ? 'var(--td-green)' : 'var(--td-red)' }}>{diffLabel}</div>
            </div>
          );
        })}

        <div className="budget-rule-card" style={{ borderLeftColor: '#CFBD91' }}>
          <div className="budget-rule-header">
            <div>
              <div className="budget-rule-label" style={{ color: '#CFBD91' }}>Discretionary <span className="budget-rule-target-pct">(not in rule)</span></div>
              <div className="budget-rule-desc">Dining, entertainment, subscriptions, shopping, travel</div>
            </div>
            <div className="budget-rule-status" style={{ background: '#CFBD9128', color: '#7C5636' }}>{pctDisc}%</div>
          </div>
          <div style={{ fontSize: 13, marginTop: 6, color: 'var(--text-medium)' }}>
            <span style={{ color: 'var(--text-dark)', fontWeight: 600 }}>{fmt(avgDisc)}/mo</span>
            {' '}· Your flex spending outside the 70/20/10 framework
          </div>
        </div>

        <div className="viz-chart-card">
          <div className="viz-chart-title">Monthly Spending by Rule</div>
          <div className="viz-chart-sub">Stacked breakdown vs income</div>
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={monthlyData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: 'var(--text-medium)' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 9, fill: 'var(--text-medium)' }} axisLine={false} tickLine={false} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} width={34} />
              <Tooltip formatter={(v, n) => [fmt(v), n]} contentStyle={tipStyle} />
              <Legend wrapperStyle={{ fontSize: 10, paddingTop: 6 }} iconSize={8} />
              {['Needs', 'Savings', 'Debt & Giving', 'Discretionary'].map((cat, i) => (
                <Bar key={cat} dataKey={cat} stackId="a" fill={TREND_COLORS[cat]} fillOpacity={0.88}
                  radius={i === 3 ? [3, 3, 0, 0] : undefined} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>

        {insights.length > 0 && (
          <div style={{ padding: '0 12px 4px' }}>
            <div className="budget-section-label">Insights</div>
            {insights.map((ins, i) => (
              <div key={i} className="budget-insight">
                <span className="budget-insight-icon">{ins.icon}</span>
                <span className="budget-insight-text">{ins.text}</span>
              </div>
            ))}
          </div>
        )}

        <div className="viz-footer" style={{ margin: '0 16px 28px' }}>
          <span>70/20/10 Rule · IVA Demo · Synthetic Data</span>
          {loadedProfileData?.name && <span>Customer: {loadedProfileData.name}</span>}
        </div>

      </div>
    </div>
  );
};

export default BudgetRuleScreen;
